import { isBiGraphSource, projectCurrentBiEvidence, withholdsUnboundBiInterpretations } from '../../../../supabase/functions/_shared/bi-v2/retrievalContext';
import { rankBiEvidence, linkedBiThemeIds } from "@/lib/governedBiRetrieval";
import { createPromptSelection, type ContextSelectionManifest } from "../../../../supabase/functions/_shared/contextAssemblyReceipt";
import type { EvidenceTimelineItem } from "@/components/coach/sessionPrep/EvidenceTimelineSection";
import type { CoachNote } from "@/hooks/useCoachNotes";
import type { CoachEvidenceCanonicalIdentity } from "@/lib/coachEvidenceResolver";
import type {
  CoachSessionPrepData,
} from "@/hooks/useCoachSessionPrepData";
import type { CoachLensSource, CoachMemorySource, CoachTwinSource } from "@/lib/coachSessionReviewContract";
import { buildCoachSelectedEvidenceDetail } from "@/lib/behavior/viewModels/buildCoachSelectedEvidenceDetail";
import { buildCoachSessionPrepIdentityRuntime } from "@/lib/behavior/viewModels/buildCoachSessionPrepIdentityRuntime";
import { buildCoachSessionPrepAttention } from "@/lib/behavior/viewModels/buildCoachSessionPrepAttention";
import { buildCoachSessionPrepEvidence } from "@/lib/behavior/viewModels/buildCoachSessionPrepEvidence";
import { buildCoachSessionPrepNoticing } from "@/lib/behavior/viewModels/buildCoachSessionPrepNoticing";
import { buildCoachSessionPrepEmergingPatterns } from "@/lib/behavior/viewModels/buildCoachSessionPrepEmergingPatterns";
import { buildCoachSessionPrepInterventionOptions } from "@/lib/behavior/viewModels/buildCoachSessionPrepInterventionOptions";
import {
  buildCoachSessionPrepThroughline,
  buildCoachSessionPrepThroughlineUnsaidSignals,
} from "@/lib/behavior/viewModels/buildCoachSessionPrepThroughline";
import { buildCoachVeeraIdentityContextFromRuntime } from "@/lib/behavior/coachVeeraIdentityContext";
import { buildLeaderArcResource } from "@/lib/behavior/viewModels/buildLeaderArcResource";
import { buildCoachSessionPrepCoachVeeraPageContext, buildCoachSessionPrepVeeraBrief } from "@/lib/behavior/viewModels/coachSessionPrepVeera";
import type { ThroughlineDayRollup } from "@/hooks/useThroughlinePresentation";
import { buildCoachSessionContinuityRuntime } from "@/lib/behavior/viewModels/buildContinuityRuntime";
import { buildLeadershipRecordItems } from "@/lib/behavior/viewModels/buildLeadershipRecordItems";

export type CoachSessionPrepRuntime = {
  contextReceiptSelection: ContextSelectionManifest;
  attention: ReturnType<typeof buildCoachSessionPrepAttention>;
  evidence: ReturnType<typeof buildCoachSessionPrepEvidence>;
  noticing: ReturnType<typeof buildCoachSessionPrepNoticing>;
  emergingPatterns: ReturnType<typeof buildCoachSessionPrepEmergingPatterns>;
  throughline: ReturnType<typeof buildCoachSessionPrepThroughline>;
  leadershipRecordItems: ReturnType<typeof buildLeadershipRecordItems>;
  selectedEvidenceDetail: ReturnType<typeof buildCoachSelectedEvidenceDetail>;
  veeraInputs: {
    fallbackBrief: ReturnType<typeof buildCoachSessionPrepVeeraBrief>;
    leaderArc: ReturnType<typeof buildLeaderArcResource>;
    interventionOptions: ReturnType<typeof buildCoachSessionPrepInterventionOptions>;
    pageContextData: ReturnType<typeof buildCoachSessionPrepCoachVeeraPageContext>;
  };
  latestCoachGrowthRecord: CoachSessionPrepData["latestCoachGrowthRecord"];
};

export type CoachSessionPrepAgreementRecord = CoachSessionPrepData["agreementRecord"];
export type CoachSessionPrepLatestGrowthRecord = CoachSessionPrepData["latestCoachGrowthRecord"];

function normalizeLeaderApprovedContextSummary(value: unknown): string | null {
  if (typeof value === "string") {
    const normalized = value.replace(/\s+/g, " ").trim();
    return normalized || null;
  }
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const record = value as Record<string, unknown>;
  return normalizeLeaderApprovedContextSummary(record.text ?? record.summary ?? record.value);
}

export function buildCoachSessionPrepUnsaidSignals(
  selectedThroughlineRollup: ThroughlineDayRollup | null,
): string[] {
  return buildCoachSessionPrepThroughlineUnsaidSignals(selectedThroughlineRollup);
}

export function buildCoachSessionPrepRuntime(params: {
  leaderId?: string;
  prepData: CoachSessionPrepData | null;
  notes: CoachNote[];
  lastSeenAt: string | null;
  recentWindowDays: number;
  agreementRecord: CoachSessionPrepData["agreementRecord"];
  selectedEvidence: (EvidenceTimelineItem & { subtype: string | null; meeting_id: string | null; canonicalIdentity?: CoachEvidenceCanonicalIdentity }) | null;
  activeCoachLens: CoachLensSource | null;
  activeCoachTwin: CoachTwinSource | null;
  activeCoachMemory: CoachMemorySource | null;
  unreadMessageCount: number;
  windowOrigin?: string;
  /** Demo leaders use synthetic transcript IDs not backed by DB rows — suppress off-page navigation. */
  isDemoLeader?: boolean;
}) {
  const prepData = params.prepData;
  const transcripts = prepData?.transcripts ?? [];
  const reflections = prepData?.reflections ?? [];
  const artifacts = prepData?.artifacts ?? [];
  const governedBi = prepData?.canonicalBehavior?.governedBi;
  const suppressLegacyInterpretation = prepData?.frameworkContextStatus === 'unavailable'
    || Boolean(governedBi && withholdsUnboundBiInterpretations(governedBi));
  const interpretationAgreements = suppressLegacyInterpretation
    ? { ...params.agreementRecord, namedAgreements: [] } : params.agreementRecord;
  // Source records stay in prepData; unbound cached prose cannot become current meaning.
  const practiceSummaries = suppressLegacyInterpretation ? [] : prepData?.practiceSummaries ?? [];
  const practiceFocusSuggestions = prepData?.practiceFocusSuggestions ?? [];
  const meetingFeedback = prepData?.meetingFeedback ?? [];
  const ledgerEvents = (prepData?.ledgerEvents ?? []).filter(event =>
    !suppressLegacyInterpretation || (event.source_type !== 'leader_chat_response'
      && event.metadata?.message_kind !== 'assistant_response' && !isBiGraphSource(event.source_type)));
  const behavioralObservation = suppressLegacyInterpretation ? null : prepData?.behavioralObservation ?? null;
  const leaderApprovedContext = (prepData?.leaderMemoryInputs ?? []).flatMap((input) => {
    const summary = normalizeLeaderApprovedContextSummary(input.approved_summary);
    if (!summary) return [];
    return [{
      title: input.title || "Approved leader context",
      memoryLabel: input.memory_label || "leader_context",
      summary,
      visibility: input.visibility,
    }];
  });
  const coachLens = prepData?.coachLens || null;
  const identityRuntime = buildCoachSessionPrepIdentityRuntime({
    prepCoachLens: coachLens,
    activeCoachLens: params.activeCoachLens,
    activeCoachTwin: params.activeCoachTwin,
    activeCoachMemory: params.activeCoachMemory,
  });
  const emptyAttention = buildCoachSessionPrepAttention({
    transcripts: [],
    unreadMessageCount: params.unreadMessageCount,
    leaderId: params.leaderId,
    isDemoLeader: params.isDemoLeader,
  });
  const emptyEvidence = buildCoachSessionPrepEvidence({
    leaderId: params.leaderId,
    transcripts: [],
    reflections: [],
    artifacts: [],
    practiceSummaries: [],
    practiceFocusSuggestions: [],
    coachLens: null,
    lastSeenAt: params.lastSeenAt,
    recentWindowDays: params.recentWindowDays,
  });
  const emptyThroughline = buildCoachSessionPrepThroughline({
    evidenceItems: [],
    notes: params.notes,
    transcripts: [],
    practiceSummaries: [],
    ledgerEvents: [],
  });
  const evidence = prepData
    ? buildCoachSessionPrepEvidence({
        leaderId: params.leaderId || prepData.profile.id,
        transcripts,
        reflections,
        artifacts,
        practiceSummaries,
        practiceFocusSuggestions,
        coachLens,
        lastSeenAt: params.lastSeenAt,
        recentWindowDays: params.recentWindowDays,
      })
    : emptyEvidence;
  const attention = prepData
    ? buildCoachSessionPrepAttention({
        transcripts,
        unreadMessageCount: params.unreadMessageCount,
        leaderId: params.leaderId,
        isDemoLeader: params.isDemoLeader,
      })
    : emptyAttention;
  const governedProjection=prepData?.frameworkContextStatus==='unavailable'?{themes:[],moments:[],slotReplacements:[]}:prepData?.canonicalBehavior?.governedBi ? projectCurrentBiEvidence(prepData.canonicalBehavior.governedBi)
    ?? (withholdsUnboundBiInterpretations(prepData.canonicalBehavior.governedBi)?{themes:[],moments:[],slotReplacements:[]}:undefined) : undefined;
  // Unbound legacy prose never competes with current correction slots.
  const noticing = prepData && !governedProjection
    ? buildCoachSessionPrepNoticing({
        leaderId: params.leaderId,
        practiceSummaries,
        meetingFeedback,
        reflections,
        transcripts,
        coachLens,
        lastSeenAt: params.lastSeenAt,
      })
    : [];
  const throughline = prepData
    ? buildCoachSessionPrepThroughline({
        evidenceItems: evidence.evidenceItems,
        notes: params.notes,
        transcripts,
        practiceSummaries,
        ledgerEvents,
      })
    : emptyThroughline;
  const latestCoachGrowthRecord = suppressLegacyInterpretation ? null : prepData?.latestCoachGrowthRecord ?? null;
  const emergingPatterns = buildCoachSessionPrepEmergingPatterns({
    behavioralObservation,
    meaningfulNoticingCards: noticing,
    latestCoachGrowthRecord,
  });
  const leadershipRecordItems = buildLeadershipRecordItems({
    leaderId: params.leaderId || prepData?.profile.id || "unknown",
    prepData,
    notes: params.notes,
  });
  const contextReceiptSelection = createPromptSelection();
  const eligibleRecords=leadershipRecordItems.filter(item=>item.visibility === "coach_only" || item.visibility === "coach_reviewed");
  const selectedSourceId=params.selectedEvidence?.canonicalIdentity?.sourceId
    ?? params.selectedEvidence?.id.replace(/^(?:transcript|reflection|artifact|practice)-|^[^:]+:/,"");
  const priority=eligibleRecords.find(item=>item.id === params.selectedEvidence?.id || item.sourceId === selectedSourceId);
  const ranking=rankBiEvidence({viewerRole:"coach",limit:4,priorityId:priority?.id,themes:governedBi?.rankThemes || [],
    candidates:eligibleRecords.map((item,index)=>{
      const related=governedBi?.observations.filter(observation=>observation.id === item.sourceId
        || observation.source_artifact_id === item.sourceId || item.evidenceSources?.includes(observation.source_artifact_id)) || [];
      const nodes=new Set((prepData?.canonicalBehavior?.contextGraph?.recentNodes || []).filter(node=>
        node.source_id === item.sourceId || item.relatedNodeIds?.includes(node.id)).map(node=>node.id));
      return {item,id:item.id,score:eligibleRecords.length-index,occurredAt:item.occurredAt || item.createdAt,
        behaviorKeys:related.map(observation=>observation.behavior_theme),
        linkedThemeIds:linkedBiThemeIds(prepData?.canonicalBehavior?.contextGraph,nodes),
        contradicted:related.some(observation=>Boolean(observation.contradicts_observation_id)
          || ["opposing","contradicts"].includes(String(observation.metadata.polarity)))};
    })});
  const briefRecordItems=ranking.biFilterState !== "no_bi_data"
    ? [...ranking.items,...leadershipRecordItems.filter(item=>!ranking.items.some(selected=>selected.id===item.id))] : leadershipRecordItems;
  const priorityLeaderId=params.leaderId??prepData?.profile.id;
  const canonicalPriority=params.selectedEvidence?.canonicalIdentity;
  const selectedTheme=governedProjection?.themes.find(theme=>theme.theme_id===params.selectedEvidence?.id);
  const fallbackBrief = buildCoachSessionPrepVeeraBrief({
    leaderId: params.leaderId,
    governedProjection,
    frameworkContextStatus:prepData?.frameworkContextStatus,
    governedPriority:selectedTheme&&priorityLeaderId?{leaderId:priorityLeaderId,themeId:selectedTheme.theme_id}:canonicalPriority&&canonicalPriority.leaderId===priorityLeaderId?canonicalPriority:undefined,
    governedProofDigest:governedBi?.proofDigest,
    frameworkPack:prepData?.frameworkPack??null,
    practiceContext:prepData?.practiceContext,
    meaningfulNoticingCards: noticing,
    agreementRecord: interpretationAgreements,
    latestCoachGrowthRecord,
    coachLens: identityRuntime.coachLensSource,
    coachTwin: identityRuntime.coachTwinSource,
    coachMemory: identityRuntime.coachMemorySource,
    leadershipRecordItems:briefRecordItems,
    coachContradictionProbes:ranking.coachContradictionProbes.slice(0,4),
    selection: contextReceiptSelection,
  });
  contextReceiptSelection.cap("prep_contradiction_probes",ranking.coachContradictionProbes.length,4);
  contextReceiptSelection.unattributed("prep_contradiction_probes",fallbackBrief.coachContradictionProbes?.length??0);
  const coachVeeraIdentity = buildCoachVeeraIdentityContextFromRuntime(identityRuntime.runtime);
  const pageContextData = buildCoachSessionPrepCoachVeeraPageContext({
    runtimeStatus: identityRuntime.runtime.status,
    coachIdentityStatus: identityRuntime.runtime.identity.activation.status,
    coachIdentityLensLabel: coachVeeraIdentity.lensLabel,
    coachIdentityFrameworkPackId: coachVeeraIdentity.frameworkPackId,
    coachIdentityFrameworkPackLabel: coachVeeraIdentity.frameworkPackLabel,
    coachIdentityVoiceSummary: coachVeeraIdentity.voiceSummary,
    coachIdentityVoiceCustomizationStatus: coachVeeraIdentity.voiceCustomizationStatus,
    coachIdentityReferenceCount: coachVeeraIdentity.referenceCount,
    coachIdentityParsedReferenceCount: coachVeeraIdentity.parsedReferenceCount,
    coachIdentityPreferredLanguage: coachVeeraIdentity.preferredLanguage,
    coachIdentityAvoidLanguage: coachVeeraIdentity.avoidLanguage,
    coachIdentityGuardrails: coachVeeraIdentity.guardrails,
    leaderLeadershipLens: prepData?.onboarding?.leadership_lens ?? null,
    leaderApprovedContext,
    continuityHighlights: [
      ...(!behavioralObservation?.isFallback && behavioralObservation?.moment ? [behavioralObservation.moment] : []),
      ...interpretationAgreements.namedAgreements,
      ...(latestCoachGrowthRecord?.workedOn || []),
      ...(latestCoachGrowthRecord?.direction || []),
      ...(latestCoachGrowthRecord?.nextConversationSignals || []),
    ],
    fallbackTopics: [
      fallbackBrief.primaryFocus,
      ...fallbackBrief.whatChanged,
      ...fallbackBrief.coachingMoves,
    ],
  });
  const interventionOptions = buildCoachSessionPrepInterventionOptions({
    brief: fallbackBrief,
  });
  const leaderArc = buildLeaderArcResource({
    brief: fallbackBrief,
    continuity: buildCoachSessionContinuityRuntime({
      namedAgreements: interpretationAgreements.namedAgreements,
      latestCoachGrowthRecord,
    }),
  });

  const runtime: CoachSessionPrepRuntime = {
    contextReceiptSelection: contextReceiptSelection.manifest,
    attention,
    evidence,
    noticing,
    emergingPatterns,
    throughline,
    leadershipRecordItems,
    selectedEvidenceDetail: buildCoachSelectedEvidenceDetail({
      evidence: params.selectedEvidence,
      windowOrigin: params.windowOrigin,
    }),
    veeraInputs: {
      fallbackBrief,
      leaderArc,
      interventionOptions,
      pageContextData,
    },
    latestCoachGrowthRecord,
  };
  return runtime;
}
